import type PIXI from 'pixi.js';
import { Transform, Rectangle, Sprite, Texture } from 'pixi.js';
import {
    BooleanAttribute,
    NumberAttribute,
    StringAttribute
} from '../../attributes';
import type { BaseAssetSettings, CopyPasteData } from '../../asset/BaseAsset';
import { BaseAsset } from '../../asset/BaseAsset';
import type { SeekMode } from '../../../enums';
import { TextAlignment, ValueMapMode } from '../../../enums';
import { assertMinimum, assertNumber } from '../../../validators';
import { getGlobalFontManager } from '../../text/FontManager';
import type {
    AssetTemplateReferenceMap,
    Pixels
} from '../../../types';
import type { CanvasRenderer, CanvasRendererSettings } from '../renderer/CanvasRenderer';
import type { FontLoader } from '../../text/FontLoader';
import type { CanvasTextAssetStyle } from '../../canvasText/asset/CanvasTextAssetStyle';
import { objectHasTemplateValues } from '../../attributes/template/common';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';


export abstract class BaseTextCanvasAsset<Renderer extends CanvasRenderer> extends BaseAsset {
    override _classType = 'CanvasCaptionAsset';

    @NumberAttribute({ min: 0, serialize: false })
    startTime = 0;

    @NumberAttribute({ min: 0, serialize: false })
    endTime = 0;

    @NumberAttribute()
    textSize = 80;

    @StringAttribute({ choices: Object.values(TextAlignment) })
    textAlign = TextAlignment.LEFT;

    /**
     * Multiplier used to calculate the final leading or line height (fontSize * (1 + leadingMultiplier)).
     */
    @NumberAttribute({ min: -2, max: 2 })
    leadingMultiplier = 0.1;
    /**
     * The scale factor to apply to the text. This is used to automatically change the font size when changing the
     * aspect ratio of the root without having to manually change the font size.
     **/
    @NumberAttribute()
    textScaleFactor = 1;

    @NumberAttribute({ min: 0, max: Infinity })
    maxHeight = Infinity;

    /** Display the active bounds for visual debugging. */
    @BooleanAttribute({ serialize: false })
    showActiveBounds = false;

    /** Display the maximum dimensions for visual debugging. */
    @BooleanAttribute({ serialize: false })
    showMaxBounds = false;

    // TODO: figure out if we can just replace this with maxWidth
    #wordWrap = false;
    #wordWrapWidth = Infinity;

    #fontManager = getGlobalFontManager();

    #renderer: Renderer = null;
    #texture: Texture = null;
    #sprite: Sprite = null;

    constructor(container, settings: BaseAssetSettings, renderer: Renderer) {
        super(container, settings);

        this.#renderer = renderer;
        this.#texture = Texture.from(this.#renderer.canvas);
        this.#sprite = new Sprite(this.#texture);
        this._addInternalChild(this.#sprite);

        if (this.maxHeight === Infinity) {
            this.maxHeight = this.root.referenceResolution.height;
        }
        if (this.maxWidth === Infinity) {
            this.maxWidth = this.root.referenceResolution.width;
        }
    }

    get fontManager() {
        return this.#fontManager;
    }

    /**
     * Some functionalities of this base class need access to style information. One
     * example is template reference map. Child classes should return whatever styles they have.
     */
    abstract get _styles(): Record<string, CanvasTextAssetStyle>;

    get renderer(): Renderer {
        return this.#renderer;
    }

    /**
     * Traditionally PIXI containers calculate the world bounds based on their children. We don't want to do this for
     * our text as otherwise it would include our sprite container which covers the full size of the video. Instead, we
     * only want to consider the bounds of the text itself. This is done by overriding the calculateBounds method and
     * calculating it manually.
     */
    override calculateBounds() {
        // override the default behavior, so we don't pick up the sprite bounds which cover the full video
        this._bounds.clear();
        this._calculateBounds();
        this._bounds.updateID = this._boundsID;
    }

    /**
     * PIXI native method for calculating bounds. We override this method to calculate the bounds of the text asset
     * which is being rendered by the canvas based renderer.
     */
    override _calculateBounds() {
        // get the world position of the container
        const transform = new Transform();
        if (this.parent?.transform) {
            const x = this.parent.transform.worldTransform.tx;
            const y = this.parent.transform.worldTransform.ty;
            transform.worldTransform.translate(x, y);
        }

        // apply the local transformation
        const localBounds = this.#calculateLocalBounds();
        const width = localBounds.width;
        const height = localBounds.height;
        transform.worldTransform.translate(localBounds.x, localBounds.y);

        this._bounds.addFrame(transform, 0, 0, width, height);
    }

    #calculateLocalBounds(): Rectangle {
        let x = this.transform.localTransform.tx;
        let y = this.transform.localTransform.ty;
        if (!this.renderer.canRender) {
            return new Rectangle(x, y, 0, 0);
        }

        const { width: rwidth, height: rheight } = this.renderer.totalBounds;
        const width = rwidth / this.referenceToOutputMultiplier;
        const height = rheight / this.referenceToOutputMultiplier;

        // consider the anchor - note that the renderer first positions just the text and then draws additional
        // graphics, so we use the text bounds to calculate the anchor offset
        const { width: tWidth, height: tHeight } = this.renderer.activeTextBounds;
        const textWidth = tWidth / this.referenceToOutputMultiplier;
        const textHeight = tHeight / this.referenceToOutputMultiplier;
        const anchorXOffset = textWidth * this.transformation.anchor.x;
        const anchorYOffset = textHeight * this.transformation.anchor.y;
        x -= anchorXOffset;
        y -= anchorYOffset;

        // consider the offset between the total bounds and the text bounds (this would be from graphics)
        const yOffset = (this.renderer.activeTextBounds.y - this.renderer.totalBounds.y) / this.referenceToOutputMultiplier;
        const xOffset = (this.renderer.activeTextBounds.x - this.renderer.totalBounds.x) / this.referenceToOutputMultiplier;
        x -= xOffset;
        y -= yOffset;

        return new Rectangle(x, y, width, height);
    }

    /**
     * Since the transformation information is spread between PIXI's container and the renderer, we need to manually
     * calculate the local bounds of the text asset. This is done by calculating the bounds of the renderer and then
     * converting it to the local space of the container.
     */
    override getLocalBounds = () => {
        this.updateTransform();

        if (!this.renderer.canRender) {
            return new Rectangle(
                this.transform.localTransform.tx,
                this.transform.localTransform.ty,
                0,
                0
            );
        }
        return this.#calculateLocalBounds();
    };

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        this.x = this.transformation.position.x + offsetX;
        this.y = this.transformation.position.y + offsetY;
        this.rotation = this.transformation.rotation + offsetRotation;
    }

    /**
     * Traditional PIXI containers apply transformations to their children. This is not the case for our text canvas
     * assets. We need to apply the transformation to the renderer instead. We do this by negating any container
     * transformation so the PIXI container always sits in the top-left corner (0-0) and thus fills up the entire
     * scene. Once that is done we then move on to apply the transformation to the renderer. This makes it seem like
     * the text asset is being transformed as part of the PIXI hierarchy when in reality it's the renderer that is being
     * transformed.
     * */
    override updateTransform = () => {
        // get the global position of the container
        const x = this.parent.transform.worldTransform.tx + this.transform.localTransform.tx;
        const y = this.parent.transform.worldTransform.ty + this.transform.localTransform.ty;

        // update worldTransform so the container ends up in the top-left corner
        const worldTransform = new Transform();
        worldTransform.worldTransform.translate(-x, -y);
        this.transform.updateTransform(worldTransform);

        if (!this.renderer.canRender) {
            return;
        }
        // now move the world transformation into the renderer
        this.renderer.translateX = x * this.referenceToOutputMultiplier as Pixels;
        this.renderer.translateY = y * this.referenceToOutputMultiplier as Pixels;
        this.renderer.anchorX = this.transformation.anchor.x;
        this.renderer.anchorY = this.transformation.anchor.y;
    };

    /**
     * Approximate the maximum bounds of the text asset. This only considers the max area of the
     * text and does not include any graphics, which may extend beyond the text.
     */
    override get approximateMaxBounds(): Rectangle {
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = getPositionFromTransformation(transformation, this.maxWidth, this.maxHeight);
        return new Rectangle(x, y, this.maxWidth, this.maxHeight);
    }

    /**
     * Returns true if the renderer has something to render. This is used to determine if we should render or not. The
     * renderer is dependent on text metrics and style metrics. If either of these are empty, then nothing can be rendered.
     */
    get canRender(): boolean {
        return this.renderer.canRender;
    }

    abstract get hasText(): boolean;

    get maxWidth(): number {
        return this.wordWrapWidth;
    }

    set maxWidth(value: number) {
        assertNumber(value);
        assertMinimum(value, 0);
        if (this.maxWidth !== value) {
            this.#wordWrapWidth = value;
            this.#wordWrap = true;
            this.requiresRedraw = true;
        }
    }

    get wordWrap(): boolean {
        return this.#wordWrap;
    }

    get wordWrapWidth(): number {
        return this.#wordWrapWidth;
    }

    protected override _build(): void {
        if (this.visible === false || !this.hasText) {
            this.#renderer.resetBuild();
            this._emitAssetCanPlay();
            return;
        }

        void this.ensureUsedFontsAreAdded();
        if (!this.isDoneTryingToLoad) {
            this.sentCanPlay = false;
            return;
        }

        const state = this.getCanvasRendererSettings();
        this.#renderer.apply(state);
        this.#renderer.build();

        // we render our text at the output resolution so we need to scale the texture up to the reference resolution here
        this.#texture.baseTexture.setSize(this.root.referenceResolution.width, this.root.referenceResolution.height);

        // apply transformation
        this.applyTransformation();

        this._emitAssetCanPlay();
    }

    rebuildContext() {
        this.renderer.rebuildContext();
        (this.#texture.baseTexture.resource as PIXI.CanvasResource).source = this.renderer.canvas;
    }

    override get isDoneTryingToLoad(): boolean {
        if (this.skipBuildAndDraw) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        if (this.allFontsAreLoaded()) {
            return true;
        }
        return false;
    }

    override get shouldLoad(): boolean {
        if (!this.visible) {
            return false;
        }
        if (typeof this.fetchResources !== 'boolean') {
            return this.fetchResources && this.fetchResources.fonts;
        }
        return super.shouldLoad;
    }

    /** @inheritDoc */
    override hasTemplateReferenceValues() {
        return Object.values(this._styles)
            .some(style => objectHasTemplateValues(style));
    }

    /**
     *  Ensures all the fonts used by this asset are added to the font manager.
     *  Returns a list of all used fonts.
     */
    ensureUsedFontsAreAdded(): FontLoader[] {
        return Object.values(this._styles).map(style => this.fontManager.addFont(style.fontUrl));
    }

    /**
     * Resolves once all the fonts used by this asset are loaded.
     */
    async onUsedFontsLoaded(): Promise<void[]> {
        return Promise.all(Object.values(this._styles).map(async(style) => {
            await this.fontManager.getFontBySourceOnceLoaded(style.fontUrl);
        }));
    }

    allFontsAreLoaded(): boolean {
        return this.ensureUsedFontsAreAdded().every(font => font.isLoaded);
    }

    someFontsDidNotLoadYet(): boolean {
        return this.ensureUsedFontsAreAdded().some(font => !font.isLoaded);
    }

    someFontsFailedToLoad(): boolean {
        return this.ensureUsedFontsAreAdded().some(font => font.failedToLoad);
    }

    usedFontWasAdded(sourceUrl: string): boolean {
        return this.ensureUsedFontsAreAdded().some(font => font.sourceUrl === sourceUrl);
    }

    override get failedToLoad(): boolean {
        if (this.skipBuildAndDraw) {
            return false;
        }
        if (!this.hasText) {
            return false;
        }
        if (this.someFontsDidNotLoadYet()) {
            return true;
        }
        return this.someFontsFailedToLoad();
    }

    override get isLoaded(): boolean {
        if (this.skipBuildAndDraw) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        return this.allFontsAreLoaded();
    }

    _redrawOnFontLoad(sourceUrl: string) {
        if (!this.usedFontWasAdded(sourceUrl)) {
            return;
        }
        // A new font has loaded, we need to rebuild the canvas, so it can access the new font.
        this.rebuildContext();

        this.requiresRedraw = true;
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    protected getCanvasRendererSettings(): CanvasRendererSettings {
        return {
            canvasWidth: (this.root.referenceResolution.width * this.referenceToOutputMultiplier) as Pixels,
            canvasHeight: (this.root.referenceResolution.height * this.referenceToOutputMultiplier) as Pixels,
            maxTextWidth: (this.wordWrapWidth * this.referenceToOutputMultiplier) as Pixels,
            maxTextHeight: (this.maxHeight * this.referenceToOutputMultiplier) as Pixels,
            showActiveBounds: this.showActiveBounds,
            showMaxBounds: this.showMaxBounds,
        };
    }

    /**
     * Override applyStyleProperties to clear dynamic styling before loading new style
     */
    override pasteStyleProperties(settings: CopyPasteData) {
        Object.values(this._styles).map(style => (style as CanvasTextAssetStyle).removeDynamicStyles());
        super.pasteStyleProperties(settings);
    }

    /**
     * Builds a map of all values referencing template value in object and its styles.
     */
    override buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        let result = super.buildTemplateReferenceMap();
        Object.entries(this._styles)
            .forEach(([styleName, style]) => {
                result = { ...style._buildValueMap(ValueMapMode.GET_REFERENCE_VALUES, styleName), ...result };
            });
        return result;
    }

    /**
     * Builds a map of color values of all color graphics in the object (including all color graphics of all styles in
     * the object). When no graphics are found, an empty object is returned.
     */
    override buildColorGraphicsMap() {
        let result = super.buildColorGraphicsMap();
        const colorMap = {};

        Object.entries(this._styles).forEach(([styleName, style]) => {
            const map = style._buildValueMap(ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE, styleName);
            result = {
                ...result,
                ...map,
            };
        });

        Object.entries(result).map(([valueKey, value]) => {
            if ((valueKey.toLowerCase().includes('fill') || valueKey.toLowerCase().includes('color'))
                && !valueKey.toLowerCase().includes('alpha')
                && valueKey.toLowerCase().includes('graphics')
            ) {
                colorMap[valueKey] = value;
            }
        });

        return colorMap;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        if (this.visible === false || !this.hasText) {
            return;
        }
        const duration = this.endTime - this.startTime;
        this.#renderer.seekMode = seekMode;
        this.#renderer.currentTime = (this.startTime + (progress * duration));
        this._draw();
    }

    override _draw() {
        if (this.visible === false) {
            return;
        }

        // NOTE: even though updateTransform is called as part of the PIXI render loop, we need to manually call it here
        // beforehand so our renderer can render the text at the correct position before it gets drawn by PIXI. That's
        // because updateTransform decomposes the transformation and transfers it to the canvas renderer.
        this.updateTransform();

        this.#renderer.draw();

        this.#sprite.texture.baseTexture.update();
    }

    override get propertiesToSerialize() {
        // WARNING: we can't use getAttributesToSerialize on base classes because it will return an empty array for all
        // child classes. This is limitation of current attribute system. That's why we need to manually add them here.
        return super.propertiesToSerialize.concat([
            'maxWidth',
            'maxHeight',
            'textSize',
            'textAlign',
            'leadingMultiplier',
            'textScaleFactor',
        ]);
    }
}
